/**
 * 智能体HTTP API服务
 *
 * 封装智能体相关的REST API调用，用于智能体CRUD操作。
 * 使用 HttpOnly Cookie 进行认证，浏览器自动携带 Token。
 */

import type {
  Agent,
  AgentCreate,
  AgentUpdate,
  ToolsListResponse,
  ToolInfo,
  SubagentInfo,
  SubagentListResponse,
} from '../types/agent';
import { ALL_TOOLS, getToolDescription, getToolCategory } from '../types/agent';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class UnauthorizedError extends HttpError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class NotFoundError extends HttpError {
  constructor(message: string = '资源不存在') {
    super(message, 404, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class ForbiddenError extends HttpError {
  constructor(message: string = '没有权限执行此操作') {
    super(message, 403, 'FORBIDDEN');
    this.name = 'ForbiddenError';
  }
}

interface AgentListResponse {
  agents: Agent[];
  total: number;
}

function getHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
  }

  switch (response.status) {
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 403:
      throw new ForbiddenError(errorMessage);
    case 404:
      throw new NotFoundError(errorMessage);
    default:
      throw new HttpError(errorMessage, response.status);
  }
}

export class AgentApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async listAgents(): Promise<Agent[]> {
    const response = await fetch(`${this.baseUrl}/api/agents`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data: AgentListResponse = await response.json();
    return data.agents;
  }

  async getAgent(agentId: number): Promise<Agent> {
    const response = await fetch(`${this.baseUrl}/api/agents/${agentId}`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async createAgent(data: AgentCreate): Promise<Agent> {
    const response = await fetch(`${this.baseUrl}/api/agents`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async updateAgent(agentId: number, data: AgentUpdate): Promise<Agent> {
    const response = await fetch(`${this.baseUrl}/api/agents/${agentId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async deleteAgent(agentId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/agents/${agentId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }
  }

  async getToolsList(agentId: number): Promise<ToolsListResponse> {
    const response = await fetch(`${this.baseUrl}/api/agents/${agentId}/tools`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 获取可用的子智能体列表
   */
  async listSubagents(): Promise<SubagentInfo[]> {
    const response = await fetch(`${this.baseUrl}/api/agents/subagents`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data: SubagentListResponse = await response.json();
    return data.subagents;
  }

  /**
   * 获取子智能体会话状态
   */
  async getSubagentStatus(sessionId: string): Promise<{
    session_id: string;
    status: 'pending' | 'running' | 'completed' | 'failed' | 'timeout';
    result?: string;
    error?: string;
    duration?: number;
  }> {
    const response = await fetch(`${this.baseUrl}/api/agents/subagents/${sessionId}/status`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  getDefaultToolsList(): ToolsListResponse {
    const tools: ToolInfo[] = ALL_TOOLS.map(name => ({
      name,
      description: getToolDescription(name),
      category: getToolCategory(name),
    }));

    return {
      tools,
      read_tools: [...ALL_TOOLS].filter(t => getToolCategory(t) === 'read') as any,
      write_tools: [...ALL_TOOLS].filter(t => getToolCategory(t) === 'write') as any,
    };
  }
}

export const agentApi = new AgentApiService();

export default agentApi;
